// Formularz tworzenia nowej oferty (modal w Panelu Handlowca, lista ofert klienta)

import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Button as MuiButton, TextField, MenuItem, Skeleton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import {
  usePostSalesCrmOffer,
  useGetSalesCrmOfferNewFormData,
  useGetSalesCrmOfferTypes,
  transformApiErrorsToFormik
} from 'api';
import { useAppNavigate } from 'hooks';
import { ICommandResponseError } from 'api/types';

interface IProps {
  onCancel: () => void;
  onSuccess: () => void;
}

interface IOfferFormValues {
  number: string;
  status: number;
  // Id typu oferty ze slownika (sales_crm_offer_type). 0 = nie wybrany.
  offerTypeId: number;
}

// Przy tworzeniu nowej oferty pokazujemy tylko statusy startowe.
// Statusy koncowe (Odrzucona/Wygasla/Anulowana) ustawia sie pozniej w edycji oferty.
const STATUS_OPTIONS = [
  { value: 1, labelKey: 'W negocjacji' },
  { value: 2, labelKey: 'Zaakceptowana' }
];

const inputSx: SxProps<Theme> = {
  '& .MuiInputLabel-root': { lineHeight: 1 },
  '& .MuiInputLabel-root.Mui-focused': { lineHeight: 'normal' },
  '& .MuiInputBase-root': { minHeight: 44, display: 'flex', alignItems: 'center' }
};

/**
 * Wyciąga błędy walidacji pól z odpowiedzi backendu.
 * Backend zwraca błędy w `fields_info[]` lub `error_fields[]` (zależnie od endpointu).
 */
const extractFieldErrors = (
  error: ICommandResponseError
): Record<string, string> => {
  const errorFields =
    error.error_fields ??
    error.fields_info?.map((item) => ({
      property_path: item.property_path,
      message: item.message,
      invalid_value: item.invalid_value
    }));

  return transformApiErrorsToFormik(errorFields);
};

const isNumberCollision = (error: ICommandResponseError): boolean => {
  const fields = error.fields_info ?? error.error_fields ?? [];
  return fields.some((f) => f.property_path === 'number');
};

const OfferForm: FC<IProps> = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const clientId = id ? Number(id) : 0;

  // Pobierz proponowany numer i defaultowy status z backendu.
  const {
    data: prefillData,
    isLoading: isPrefillLoading,
    refetch: refetchPrefill
  } = useGetSalesCrmOfferNewFormData();

  // Pobierz slownik typow oferty (na chwile obecna 1 opcja, w przyszlosci wiecej).
  const { data: offerTypesData, isLoading: isOfferTypesLoading } =
    useGetSalesCrmOfferTypes();
  const offerTypes = offerTypesData?.items ?? [];

  // Komunikat sukcesu/błędu wyświetla globalnie axios.ts na podstawie `message` z backendu.
  const { mutate: createOffer, isLoading: isCreating } = usePostSalesCrmOffer({
    onSuccess: (response) => {
      onSuccess();
      onCancel();

      // Przejdź do nowo utworzonej oferty.
      const createdOfferId = (response?.data as { id?: number } | undefined)?.id;
      if (clientId && createdOfferId) {
        navigate(`/trader/clients/${clientId}/offers/${createdOfferId}`);
      }
    },
    onError: async (error) => {
      setErrors(extractFieldErrors(error));

      // Kolizja na polu `number` (inny handlowiec zdążył wziąć ten sam numer) –
      // ponów GET i podstaw świeży, wyższy numer.
      if (isNumberCollision(error)) {
        const { data: fresh } = await refetchPrefill();
        if (fresh?.number) {
          setFieldValue('number', fresh.number);
        }
      }
    }
  });

  const { values, handleChange, handleBlur, touched, errors, setErrors, setFieldValue, handleSubmit } =
    useFormik<IOfferFormValues>({
      initialValues: {
        number: '',
        status: 1,
        offerTypeId: 0
      },
      enableReinitialize: false,
      onSubmit: (formValues) => {
        if (!clientId) {
          return;
        }
        createOffer({
          client_id: clientId,
          number: formValues.number,
          status: formValues.status,
          offer_type_id: formValues.offerTypeId > 0 ? formValues.offerTypeId : null
        });
      }
    });

  // Podstaw dane z backendu po ich załadowaniu (tylko raz – nie nadpisuj ręcznych zmian usera).
  useEffect(() => {
    if (prefillData && !values.number) {
      setFieldValue('number', prefillData.number);
      setFieldValue('status', prefillData.status ?? 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Auto-wybor typu oferty: domyslnie zaznaczamy pierwsza opcje ze slownika.
  // Uzytkownik moze ja zmienic, jesli dostepnych jest wiecej typow.
  useEffect(() => {
    if (offerTypes.length > 0 && values.offerTypeId === 0) {
      setFieldValue('offerTypeId', offerTypes[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerTypes.length]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keyup', handleKeyPress);
    return () => document.removeEventListener('keyup', handleKeyPress);
  }, [onCancel]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 360 }}>
      <h2 style={{ margin: 0 }}>{t('Nowa oferta')}</h2>

      {isPrefillLoading ? (
        <Skeleton variant="rounded" height={44} />
      ) : (
        <TextField
          fullWidth
          color="secondary"
          sx={inputSx}
          id="number"
          name="number"
          label={t('Numer oferty')}
          value={values.number}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.number && errors.number)}
          helperText={
            (touched.number && errors.number) ||
            t('Numer wygenerowany automatycznie. W razie potrzeby możesz go zmienić.')
          }
          InputProps={{ readOnly: false }}
          required
        />
      )}

      {isOfferTypesLoading ? (
        <Skeleton variant="rounded" height={44} />
      ) : (
        <TextField
          select
          fullWidth
          color="secondary"
          sx={inputSx}
          id="offerTypeId"
          name="offerTypeId"
          label={t('Typ oferty')}
          value={values.offerTypeId || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.offerTypeId && errors.offerTypeId)}
          helperText={
            (touched.offerTypeId && errors.offerTypeId) ||
            (offerTypes.length === 1 ? t('Domyslny typ oferty.') : undefined)
          }
          disabled={offerTypes.length === 0}>
          {offerTypes.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        fullWidth
        color="secondary"
        sx={inputSx}
        id="status"
        name="status"
        label={t('Status')}
        value={values.status}
        onChange={handleChange}
        onBlur={handleBlur}>
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </TextField>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <MuiButton type="button" variant="outlined" color="secondary" onClick={onCancel} disabled={isCreating}>
          {t('Anuluj')}
        </MuiButton>
        <MuiButton
          type="submit"
          variant="contained"
          color="secondary"
          disabled={isCreating || isPrefillLoading || !values.number}>
          {t('Utwórz ofertę')}
        </MuiButton>
      </div>
    </form>
  );
};

export default OfferForm;
